import {AnimatePresence, motion} from 'framer-motion';
import {
  Box,
  getThemeValue,
  Heading,
  Hide,
  HStack,
  Show,
  Space,
  ThemeZIndexPath,
  useFoundationTheme,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

// React's KeyboardEvent is aliased so it does not shadow the DOM KeyboardEvent
// used by the window keydown listener in registerEscapeKeydown
import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import {isNil, isNotNil, not} from 'ramda';

import {
  findFirstFocusableElement,
  RequiredTestIdProps,
  suffixTestId,
  TestIdProps,
  useLatest,
} from 'shared/web';

import {ButtonProps} from '../Button/Button';
import {ButtonGroup} from '../ButtonGroup/ButtonGroup';
import {IconButton} from '../IconButton/IconButton';
import {Pagination, PaginationProps} from '../Pagination/Pagination';
import {Portal} from '../Portal/components/Portal';
import {DialogCloseGuard, DialogCloseGuardContext} from './components/DialogCloseGuardContext';
import {DialogTransition} from './components/DialogTransition';
import {DialogSize} from './types/DialogSize';
import {ScrollBehavior} from './types/ScrollBehavior';

const DEFAULT_SCROLL_BEHAVIOR = 'inside';
const DIALOG_CLOSE_BUTTON_SELECTOR = '[data-testid*="dialogCloseButton"]';
const DIALOG_HEIGHT_BREAKPOINT_PX = 1000;

type DialogPaginationProps = Pick<PaginationProps, 'pagesQuantity' | 'onPageChange' | 'page'>;

export interface DialogProps extends TestIdProps {
  isOpen: boolean;
  children: ReactNode;
  title?: string;
  scrollBehavior?: ScrollBehavior;
  size?: DialogSize;
  withAdditionalFooter?: boolean;
  id?: string;
  disableBodyPadding?: boolean;
  disableMaxHeight?: boolean;
  hideCloseButton?: boolean;
  /**
   * @about Displays sticky footer with buttons inside
   */
  buttons?: ButtonProps[];
  onClose?: () => void;
  onCloseComplete?: () => void;
  /**
   * If `true`, clicking the overlay outside the dialog panel calls `onClose`. Opt-in so that
   * existing dialogs keep requiring an explicit close; dialogs whose unsaved input must not be
   * lost to a stray click should stay opted out.
   * @default false
   */
  closeOnOverlayClick?: boolean;
  /**
   * If `true`, the modal will return focus to the element that triggered it when it closes.
   * @default true
   */
  returnFocusOnClose?: boolean;
  /**
   * @about Focuses the element whose data-testid contains this value when the
   * dialog opens, instead of the default close button. Used e.g. by
   * confirmation dialogs to focus their confirm button.
   */
  initialFocusTestId?: string;
  pagination?: DialogPaginationProps | ReactNode;
  /**
   * @about overrides z-index of the dialog so that it can be displayed above the lightbox
   */
  isInLightbox?: boolean;
}

const dialogContentVariants = cva(
  'fixed left-0 top-0 flex h-full w-screen items-center justify-center',
  {
    variants: {
      scrollBehavior: {
        inside: 'overflow-hidden p-0',
        outside: 'overflow-auto px-0 py-8',
      },
    },
  }
);

const dialogHeaderVariants = cva(
  'box-border block flex-[0_0_auto] border-b border-[var(--color-general-separator)] p-4'
);

const dialogBodyVariants = cva('block', {
  variants: {
    scrollBehavior: {
      inside: 'flex-[1_1_auto] overflow-auto',
      outside: 'max-h-none flex-[0_0_auto]',
    },
    withAdditionalFooter: {
      true: 'mb-16',
      false: 'mb-0',
    },
    disableBodyPadding: {
      true: 'p-0',
      false: 'p-4',
    },
  },
});

const dialogFooterVariants = cva('flex items-center justify-end p-0');
const styledOverlayVariants = cva(
  'fixed left-0 top-0 flex h-full w-screen items-center justify-center overflow-auto bg-[var(--color-palettes-neutral-900-20)]'
);

export function Dialog(props: DialogProps): ReactNode {
  const theme = useFoundationTheme();
  const isEnlarged = window.innerHeight <= DIALOG_HEIGHT_BREAKPOINT_PX;
  const initializedDialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  // Guards overlay-click closing against text selections that start inside the panel and end
  // on the overlay — such a drag fires a click on the overlay even though the user never
  // "clicked outside".
  const isOverlayMouseDownRef = useRef(false);
  // Set by content that must confirm before it is thrown away (useDialogCloseGuard).
  const closeGuardRef = useRef<DialogCloseGuard | null>(null);

  const requestClose = () => {
    const close = () => props.onClose?.();
    if (closeGuardRef.current?.(close) === true) {
      return;
    }
    close();
  };
  const requestCloseRef = useLatest(requestClose);
  const setGuard = (guard: DialogCloseGuard | null) => {
    closeGuardRef.current = guard;
  };

  const handleCloseButtonClick = () => {
    requestClose();
  };

  const handleOverlayMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    isOverlayMouseDownRef.current = event.target === event.currentTarget;
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (
      props.closeOnOverlayClick &&
      isOverlayMouseDownRef.current &&
      event.target === event.currentTarget
    ) {
      requestClose();
    }
  };

  const handleContentKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    // When the dialog focuses a button on open (initialFocusTestId), a chord
    // like Ctrl/⌘+Enter that opened the dialog and is still held would OS
    // auto-repeat Enter onto the focused button and activate it. Only a fresh
    // Enter press (keyup first) may confirm — auto-repeats are swallowed.
    if (isNotNil(props.initialFocusTestId) && event.key === 'Enter' && event.repeat) {
      event.preventDefault();
    }
  };

  const isOpen = props.isOpen;

  useEffect(
    function registerEscapeKeydown() {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          event.stopPropagation();

          requestCloseRef.current();
        }
      };

      window.addEventListener('keydown', onKeyDown);

      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    },
    [isOpen, requestCloseRef]
  );

  const handleDialogContentRef = (dialog: HTMLDivElement | null) => {
    if (isNil(dialog) || !props.isOpen || initializedDialogRef.current === dialog) {
      return;
    }

    // The portal's content is now attached. Ref callbacks can repeat on rerenders and in
    // Strict Mode, so initialize each opening once before focus can trigger another render.
    initializedDialogRef.current = dialog;
    const activeElement = dialog.ownerDocument.activeElement;
    if (isNil(previouslyFocusedElementRef.current)) {
      previouslyFocusedElementRef.current =
        activeElement instanceof HTMLElement ? activeElement : null;
    }

    // A descendant may already have focused itself during this commit.
    if (dialog.contains(activeElement)) {
      return;
    }

    const initialFocusElement = props.initialFocusTestId
      ? dialog.querySelector<HTMLElement>(`[data-testid*="${props.initialFocusTestId}"]`)
      : null;
    const focusTarget =
      initialFocusElement ??
      dialog.querySelector<HTMLElement>(DIALOG_CLOSE_BUTTON_SELECTOR) ??
      findFirstFocusableElement(dialog);
    focusTarget?.focus();
  };

  useLayoutEffect(
    function manageReturnFocusOnOpenChange() {
      if (props.isOpen) {
        // Capture the trigger before the portal's follow-up commit can autofocus a child.
        // When reopening during an exit animation, the ref callback may already have saved it.
        if (isNil(previouslyFocusedElementRef.current)) {
          previouslyFocusedElementRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }
        return;
      }

      if (props.returnFocusOnClose !== false) {
        previouslyFocusedElementRef.current?.focus();
      }
      previouslyFocusedElementRef.current = null;
      initializedDialogRef.current = null;
    },
    [props.isOpen, props.returnFocusOnClose]
  );

  const scrollBehavior = props.scrollBehavior ?? DEFAULT_SCROLL_BEHAVIOR;
  const zIndexPath = props.isInLightbox ? 'LIGHTBOX_MODAL' : 'MODAL';

  return (
    <AnimatePresence onExitComplete={props.onCloseComplete}>
      {/* eslint-disable-next-line eag/prefer-show -- AnimatePresence requires a direct conditional child for exit animations */}
      {props.isOpen && (
        <Portal>
          <StyledOverlay zIndex={zIndexPath} data-testid={suffixTestId('dialogOverlay', props)}>
            <div
              role="dialog"
              ref={handleDialogContentRef}
              onMouseDown={handleOverlayMouseDown}
              onClick={handleOverlayClick}
              onKeyDown={handleContentKeyDown}
              data-testid={suffixTestId('dialogContent', props)}
              className={cn(dialogContentVariants({scrollBehavior}))}
              style={{zIndex: theme.zIndices[zIndexPath]}}
            >
              <DialogTransition
                size={props.size}
                isEnlarged={isEnlarged}
                scrollBehavior={scrollBehavior}
                disableMaxHeight={props.disableMaxHeight}
              >
                <Show when={props.title}>
                  <div
                    className={cn(dialogHeaderVariants())}
                    data-testid={suffixTestId('dialogHeader', props)}
                  >
                    <HStack spacing={4} justify="space-between" align="center">
                      <Heading size={4}>{props.title}</Heading>
                      <HStack>
                        {isDialogPaginationProps(props.pagination) ? (
                          <Pagination
                            {...props.pagination}
                            variant="text-only"
                            showPrevButton
                            showNextButton
                            data-testid={suffixTestId('dialogHeaderPagination', props)}
                          />
                        ) : (
                          props.pagination
                        )}
                        <Space horizontal={2} />
                        <Hide when={props.hideCloseButton}>
                          <IconButton
                            data-testid={suffixTestId('dialogCloseButton', props)}
                            icon="navigation/close"
                            aria-label="closeDialog"
                            onClick={handleCloseButtonClick}
                          />
                        </Hide>
                      </HStack>
                    </HStack>
                  </div>
                </Show>

                <div
                  className={cn(
                    dialogBodyVariants({
                      scrollBehavior,
                      withAdditionalFooter: Boolean(props.withAdditionalFooter),
                      disableBodyPadding: Boolean(props.disableBodyPadding),
                    })
                  )}
                  data-testid={suffixTestId('dialogBody', props)}
                >
                  <DialogCloseGuardContext.Provider value={{setGuard, requestClose}}>
                    {props.children}
                  </DialogCloseGuardContext.Provider>
                </div>

                <Show when={props.buttons && not(props.withAdditionalFooter)}>
                  <div
                    className={cn(dialogFooterVariants())}
                    data-testid={suffixTestId('dialogFooter', props)}
                  >
                    <Box padding={4}>
                      <ButtonGroup buttons={props.buttons} />
                    </Box>
                  </div>
                </Show>
              </DialogTransition>
            </div>
          </StyledOverlay>
        </Portal>
      )}
    </AnimatePresence>
  );
}

interface StylesOverlayProps extends RequiredTestIdProps {
  zIndex: ThemeZIndexPath;
  children: ReactNode;
}

function StyledOverlay(props: StylesOverlayProps) {
  const theme = useFoundationTheme();

  return (
    <motion.div
      className={cn(styledOverlayVariants())}
      style={{zIndex: getThemeValue<string | number>(theme.zIndices, props.zIndex)}}
      initial="hidden"
      animate="visible"
      exit="hidden"
      data-testid={props['data-testid']}
      variants={{
        visible: {
          opacity: 1,
          transition: {
            duration: 0.3,
            ease: [0.3, 0.1, 0.75, 0.9],
          },
        },
        hidden: {
          opacity: 0,
          transition: {
            duration: 0.1,
            ease: [0.9, 0.75, 0.1, 0.3],
          },
        },
      }}
    >
      {props.children}
    </motion.div>
  );
}

function isDialogPaginationProps(
  pagination: DialogPaginationProps | ReactNode
): pagination is DialogPaginationProps {
  return typeof pagination === 'object' && isNotNil(pagination) && 'pagesQuantity' in pagination;
}
